import { getDetails, LessonsAccessStatus, PresentationResource } from 'api/lessonsApi';
import { endpoints } from 'endpoints.config';

export type ResourcesAccess = {
  bFullAccess: boolean;
  status: LessonsAccessStatus;
  bFreeTrial: boolean;
};

export type ResourceCategoryTile = {
  resourceCategoriesId: number;
  name: string;
  description: string | null;
  colorCSS: string | null;
  imagePath: string | null;
};

export type Resource = {
  resourcesId: number;
  name: string;
  resourceType: string;
  bDownloadable: boolean;
  bCanOpen: boolean;
};

export type ResourceCategories = {
  access: ResourcesAccess;
  resourceCategories: ResourceCategoryTile[];
};

export type ResourceCategory = ResourceCategoryTile & {
  parentResourceCategoriesId: number | null;
  resources: Resource[];
  childResourceCategories: ResourceCategoryTile[];
};

export type ResourcesActivity = {
  id: number;
  name: string;
  strippedName: string;
  bIsGroup: boolean;
  colorCSS: string | null;
  imagePath: string | null;
};

export type ResourceActivity = {
  activity: ResourcesActivity;
  resources: Resource[];
};

export type ResourcePresentation = {
  resourcesId: number;
  name: string;
  slides: PresentationResource[];
};

export type ResourceFile = {
  resourcesId: number;
  name: string;
  resourceType: string;
  filePath: string;
};

export type ResourcesTileType = 'ResourceCategory' | 'Activity' | 'ActivityGroup';

export type ResourceSearch = {
  resources: Resource[];
  tiles: {
    id: number;
    name: string;
    colorCSS: string | null;
    tileType: ResourcesTileType;
    imagePath: string | null;
  }[];
};

export class ResourcesApi {
  public static getCategories = (subjectsId: number) =>
    getDetails<ResourceCategories>(endpoints.resources.categories, { subjectsId });

  public static getActivities = (subjectsId: number) =>
    getDetails<{ activities: ResourcesActivity[] }>(endpoints.resources.activities, {
      subjectsId,
    });

  public static getCategory = (subjectsId: number, resourceCategoriesId: number) =>
    getDetails<ResourceCategory>(endpoints.resources.category, {
      subjectsId,
      resourceCategoriesId,
    });

  public static getActivity = (subjectsId: number, activitiesId: number) =>
    getDetails<ResourceActivity>(endpoints.resources.activity, { subjectsId, activitiesId });

  public static getActivityGroup = (subjectsId: number, activityGroupsId: number) =>
    getDetails<ResourceActivity>(endpoints.resources.activityGroup, {
      subjectsId,
      activityGroupsId,
    });

  public static getPresentation = (subjectsId: number, resourcesId: number) =>
    getDetails<ResourcePresentation>(endpoints.resources.presentation, {
      subjectsId,
      resourcesId,
    });

  /**
   * A time-limited link to a resource's file. Each call counts a view or a download.
   */
  public static getFile = (subjectsId: number, resourcesId: number, bDownload: boolean) =>
    getDetails<ResourceFile>(endpoints.resources.file, { subjectsId, resourcesId, bDownload });

  public static search = (subjectsId: number, searchTerm: string) =>
    getDetails<ResourceSearch>(endpoints.resources.search, { subjectsId, searchTerm });
}
